import { useCallback, useState } from 'react';

import { splitTextByUrl } from '../utils/linkify.js';

/**
 * URLをリンク化して表示し、クリックすると編集できるコメント入力欄。
 *
 * textarea の中身はリンクにできないため、閲覧時は整形テキスト、クリック時は
 * textarea と表示を切り替える。リンク自体のクリックでは編集に入らないので、
 * 遷移と編集開始が競合しない。
 */
export default function LinkableTextArea({ value, onChange, readOnly = false }) {
	const [isEditing, setIsEditing] = useState(false);

	// 入力に合わせて高さを伸ばす。既存のコメント欄と同じ挙動に揃える
	const autoResize = useCallback((element) => {
		if (!element) return;
		element.style.height = 'auto';
		element.style.height = `${element.scrollHeight}px`;
	}, []);

	const text = value || '';

	if (isEditing && !readOnly) {
		return (
			<textarea
				ref={(element) => {
					autoResize(element);
					// 閲覧表示からの切り替え直後にキャレットを置く
					if (element && document.activeElement !== element) element.focus();
				}}
				className="db-input-control db-textarea-control"
				onBlur={() => setIsEditing(false)}
				onChange={(e) => {
					onChange(e.target.value);
					autoResize(e.target);
				}}
				style={{ minHeight: '80px', whiteSpace: 'pre-wrap', overflow: 'hidden' }}
				value={text}
			/>
		);
	}

	return (
		<div
			// 読み取り専用（一覧セル）は入力欄に見せる必要がないので枠線・最低高さを外す
			className={readOnly ? 'db-linkable-text db-linkable-text--plain' : 'db-linkable-text'}
			onClick={() => {
				if (!readOnly) setIsEditing(true);
			}}
			onKeyDown={(e) => {
				if (readOnly) return;
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					setIsEditing(true);
				}
			}}
			role={readOnly ? undefined : 'textbox'}
			tabIndex={readOnly ? undefined : 0}
		>
			{splitTextByUrl(text).map((segment, index) => (
				segment.type === 'url' ? (
					<a
						// セグメントは元テキストの並び順そのものなので index で一意になる
						key={index}
						href={segment.value}
						onClick={(e) => e.stopPropagation()}
						rel="noopener noreferrer"
						target="_blank"
					>
						{segment.value}
					</a>
				) : (
					<span key={index}>{segment.value}</span>
				)
			))}
		</div>
	);
}
